import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfColor,
  PdfPen,
  PdfUnit,
} from '../../pdf/pdf';

const DEFAULT_SIZE_MM = 50;

export const clockModule: IModule = {
  name: 'clock',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    const trimmed = source.trim();
    const timeMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);

    let hours = 0;
    let minutes = 0;
    let seconds: number | undefined;
    let noTime = false;

    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
      seconds = timeMatch[3] ? parseInt(timeMatch[3], 10) : undefined;
    } else if (trimmed === '') {
      noTime = true;
    }

    return {
      type: 'clock',
      attrs: {
        ...attrs,
        hours: String(hours),
        minutes: String(minutes),
        seconds: seconds !== undefined ? String(seconds) : '',
        noTime: noTime ? 'true' : '',
      },
      content: source,
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const sizeMm = parseFloat(content.attrs.size) || DEFAULT_SIZE_MM;
    const size = PdfUnit.FromMillimeters(sizeMm);
    return new PdfSize(size, size);
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for clock drawing');
    }

    const hours = parseInt(content.attrs.hours || '0', 10);
    const minutes = parseInt(content.attrs.minutes || '0', 10);
    const seconds = content.attrs.seconds ? parseInt(content.attrs.seconds, 10) : undefined;
    const showSeconds = content.attrs.showSeconds === 'true';
    const noTime = content.attrs.noTime === 'true';

    const clockSize = Math.min(rect.width, rect.height);
    const clockRect = new PdfRect(
      rect.left + (rect.width - clockSize) / 2,
      rect.top + (rect.height - clockSize) / 2,
      clockSize,
      clockSize
    );

    try {
      if (noTime) {
        page.drawClock(clockRect, 0, 0, null, true);
      } else if (seconds !== undefined || showSeconds) {
        page.drawClock(clockRect, hours, minutes, seconds);
      } else {
        page.drawClock(clockRect, hours, minutes);
      }
    } catch (e) {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      if (noTime) {
        page.drawString('CLOCK', font, PdfBrush.Black, null, rect, ETextAlign.Center);
      } else {
        page.drawString(
          `${hours}:${minutes.toString().padStart(2, '0')}`,
          font,
          PdfBrush.Black,
          null,
          rect,
          ETextAlign.Center
        );
      }
    }
  },
};
